import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "store";
import { MicrosoftSSOResult, OnboardingState } from "./onboardingTypes";
import {
  getRestOnboardingStatus,
  getRestWelcomeLinkStatus,
  postRestSendVerificationCodeEmail,
  postRestInvalidateSessionCache,
  postRestSubmitOnboardingAdmin,
  postRestUpdateWelcomeLink,
  postRestVerifyEmailCode,
} from "@api/onboardingStore";
import {
  getWelcomeProgressLocalStorage,
  setWelcomeProgressLocalStorage,
} from "../../../auth/WelcomeHelpers";
import {
  AccountWelcomeLinkDto,
  EmailVerificationRequest,
  OnboardingStatus,
  ValidateEmailStatus,
} from "@lib/ShiOneClient";
import { EXPIRED_WELCOME_LINK_MESSAGE } from "../../../constants/onboardingMessages";
import { isAssumingUserIdentity } from "shared-ui/src/utils/clientmanagement";

function isExactExpiredInviteMessage(message: string): boolean {
  return message === "Expired invite.";
}

export function extractOnboardingErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "An unexpected server error occurred.";
  }

  const errorWithResponse = error as {
    response?: { data?: unknown };
    message?: unknown;
  };
  const responseData = errorWithResponse.response?.data;

  if (typeof responseData === "string" && responseData.trim().length > 0) {
    if (isExactExpiredInviteMessage(responseData)) {
      return EXPIRED_WELCOME_LINK_MESSAGE;
    }

    return responseData;
  }

  if (responseData && typeof responseData === "object") {
    const message = (responseData as { message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) {
      if (isExactExpiredInviteMessage(message)) {
        return EXPIRED_WELCOME_LINK_MESSAGE;
      }

      return message;
    }
  }

  if (
    typeof errorWithResponse.message === "string" &&
    errorWithResponse.message.trim().length > 0
  ) {
    return errorWithResponse.message;
  }

  return "An unexpected server error occurred.";
}

const initialState: OnboardingState = {
  onboardingRequestStatus: "idle",
  onboardingRequestError: "",
  onboardingStatus: {} as OnboardingStatus,
  welcomeLinkRequestStatus: "idle",
  welcomeLinkRequestError: "",
  welcomeLinkStatus: {} as AccountWelcomeLinkDto,
  createEmailVerificationStatus: "idle",
  createEmailVerificationError: "",
  emailVerificationStatus: {} as ValidateEmailStatus,
  validateEmailVerificationStatus: "idle",
  validateEmailVerificationError: "",
  validateEmailVerificationResult: {} as ValidateEmailStatus,
  microsoftSSOResultStatus: "idle",
  microsoftSSOResultError: "",
  microsoftSSOResult: {} as MicrosoftSSOResult,
  submitOnboardingAdminStatus: "idle",
  submitOnboardingAdminError: "",
  submitOnboardingAdminResult: {} as OnboardingStatus,
  welcomeLinkUpdateStatus: "idle",
  welcomeLinkUpdateError: "",
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    receivingWelcomeLinkStatus(state) {
      state.welcomeLinkRequestStatus = "loading";
    },
    receiveWelcomeLinkStatus(
      state,
      action: PayloadAction<AccountWelcomeLinkDto>,
    ) {
      state.welcomeLinkStatus = action.payload;
      state.welcomeLinkRequestStatus = "complete";
    },
    receiveWelcomeLinkError(state, action: PayloadAction<string>) {
      state.welcomeLinkRequestError = action.payload;
      state.welcomeLinkRequestStatus = "error";
    },
    receivingOnboardingStatus(state) {
      state.onboardingRequestStatus = "loading";
    },
    receiveOnboardingStatus(state, action: PayloadAction<OnboardingStatus>) {
      state.onboardingStatus = action.payload;
      state.onboardingRequestStatus = "complete";
    },
    receiveOnboardingError(state, action: PayloadAction<string>) {
      state.onboardingRequestError = action.payload;
      state.onboardingRequestStatus = "error";
    },
    receivingCreateEmailVerificationCode(state) {
      state.createEmailVerificationStatus = "loading";
    },
    receiveCreateEmailVerificationCode(
      state,
      action: PayloadAction<ValidateEmailStatus>,
    ) {
      state.emailVerificationStatus = action.payload;
      state.createEmailVerificationStatus = "complete";
    },
    receiveCreateEmailCodeError(state, action: PayloadAction<string>) {
      state.createEmailVerificationStatus = "error";
      state.createEmailVerificationError = action.payload;
    },
    receivingValidateEmailVerification(state) {
      state.validateEmailVerificationStatus = "loading";
    },
    receiveValidateEmailVerification(
      state,
      action: PayloadAction<ValidateEmailStatus>,
    ) {
      state.validateEmailVerificationResult = action.payload;
      state.validateEmailVerificationStatus = "complete";
    },
    receiveValidateEmailError(state, action: PayloadAction<string>) {
      state.validateEmailVerificationStatus = "error";
      state.validateEmailVerificationError = action.payload;
    },
    receiveSSOStatus(state, action: PayloadAction<MicrosoftSSOResult>) {
      state.microsoftSSOResult = action.payload;
      state.microsoftSSOResultStatus = "complete";
    },
    submittingOnboardingAdmin(state) {
      state.submitOnboardingAdminStatus = "loading";
    },
    submitOnboardingAdmin(state, action: PayloadAction<OnboardingStatus>) {
      state.submitOnboardingAdminStatus = "complete";
      state.submitOnboardingAdminResult = action.payload;
    },
    submitOnboardingAdminError(state, action: PayloadAction<string>) {
      state.submitOnboardingAdminStatus = "error";
      state.submitOnboardingAdminError = action.payload;
    },
    receivingWelcomeLinkUpdate(state) {
      state.welcomeLinkUpdateStatus = "loading";
    },
    receiveWelcomeLinkUpdate(
      state,
      action: PayloadAction<AccountWelcomeLinkDto>,
    ) {
      state.welcomeLinkUpdateStatus = "complete";
      state.welcomeLinkStatus = action.payload;
    },
    receiveWelcomeLinkUpdateError(state, action: PayloadAction<string>) {
      state.welcomeLinkUpdateStatus = "error";
      state.welcomeLinkUpdateError = action.payload;
    },
  },
});

export const getWelcomeLinkStatus =
  (guid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingWelcomeLinkStatus());
    try {
      const welcomeLinkStatus = await getRestWelcomeLinkStatus(guid);
      dispatch(
        onboardingSlice.actions.receiveWelcomeLinkStatus(welcomeLinkStatus),
      );
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.receiveWelcomeLinkError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export const getOnboardingStatus =
  (guid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingOnboardingStatus());
    try {
      const onboardingStatus = await getRestOnboardingStatus(guid);
      dispatch(
        onboardingSlice.actions.receiveOnboardingStatus(onboardingStatus),
      );
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.receiveOnboardingError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export const postCreateEmailVerificationCode =
  (emailVerificationRequest: EmailVerificationRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingCreateEmailVerificationCode());
    try {
      const emailVerificationResult = await postRestSendVerificationCodeEmail(
        emailVerificationRequest,
      );
      dispatch(
        onboardingSlice.actions.receiveCreateEmailVerificationCode(
          emailVerificationResult,
        ),
      );
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.receiveCreateEmailCodeError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export const postValidateCode =
  (emailVerificationRequest: EmailVerificationRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingValidateEmailVerification());
    try {
      const emailVerificationResult = await postRestVerifyEmailCode(
        emailVerificationRequest,
      );
      dispatch(
        onboardingSlice.actions.receiveValidateEmailVerification(
          emailVerificationResult,
        ),
      );
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.receiveValidateEmailError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export const setOnboardingStatusFromLocalStorage =
  (ssoResult: MicrosoftSSOResult): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingOnboardingStatus());
    const onboardingState = getWelcomeProgressLocalStorage();
    dispatch(onboardingSlice.actions.receiveOnboardingStatus(onboardingState));
    dispatch(onboardingSlice.actions.receiveSSOStatus(ssoResult));
  };

export const updateOnboardingStatus =
  (onboardingStatus: OnboardingStatus): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receiveOnboardingStatus(onboardingStatus));
    setWelcomeProgressLocalStorage(onboardingStatus);
  };

export const postFinalOnboardingStatus =
  (onboardingStatus: OnboardingStatus): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.submittingOnboardingAdmin());
    try {
      const postResult = await postRestSubmitOnboardingAdmin(onboardingStatus);
      if (isAssumingUserIdentity()) {
        await postRestInvalidateSessionCache();
      }
      dispatch(onboardingSlice.actions.submitOnboardingAdmin(postResult));
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.submitOnboardingAdminError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export const updateWelcomeLinkOptions =
  (welcomeLink: AccountWelcomeLinkDto): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(onboardingSlice.actions.receivingWelcomeLinkUpdate());
    try {
      const updateResult = await postRestUpdateWelcomeLink(welcomeLink);
      dispatch(onboardingSlice.actions.receiveWelcomeLinkUpdate(updateResult));
    } catch (error: unknown) {
      dispatch(
        onboardingSlice.actions.receiveWelcomeLinkUpdateError(
          extractOnboardingErrorMessage(error),
        ),
      );
    }
  };

export default onboardingSlice.reducer;
